import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Clock, HelpCircle, ChevronDown, Calendar, Globe, CreditCard, KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  use24HourFormat?: boolean;
  onTimeFormatToggle?: () => void;
  setUse24HourFormat?: (value: boolean) => void;
  onOpenCommandPalette?: () => void;
  onToggleHelp?: () => void;
  onOpenCalendarPreferences?: () => void;
  onOpenTimezoneGroups?: () => void;
}

export function Sidebar({ isCollapsed, onToggle, use24HourFormat = false, onTimeFormatToggle, setUse24HourFormat, onOpenCommandPalette, onToggleHelp, onOpenCalendarPreferences, onOpenTimezoneGroups }: SidebarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside or sidebar collapses
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdown when sidebar collapses
  useEffect(() => {
    if (isCollapsed) {
      setShowLanguageDropdown(false);
    }
  }, [isCollapsed]);

  // Language options
  const languages = [
    { code: 'en', label: 'English', shortLabel: 'EN' },
    { code: 'fr', label: 'Français', shortLabel: 'FR' },
    { code: 'es', label: 'Español', shortLabel: 'ES' },
    { code: 'de', label: 'Deutsch', shortLabel: 'DE' },
    { code: 'pt', label: 'Português', shortLabel: 'PT' },
    { code: 'zh', label: '中文', shortLabel: 'ZH' },
  ];

  // Get current language display text
  const getCurrentLangDisplay = () => {
    const lang = i18n.language;
    if (lang?.startsWith('fr')) return 'Français';
    if (lang?.startsWith('es')) return 'Español';
    if (lang?.startsWith('de')) return 'Deutsch';
    if (lang?.startsWith('pt')) return 'Português';
    if (lang?.startsWith('zh')) return '中文';
    return 'English';
  };

  // Language change function
  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    
    // Auto-set 24H format for non-English languages
    if (langCode !== 'en' && setUse24HourFormat) {
      setUse24HourFormat(true);
    }
    
    setShowLanguageDropdown(false);
  };

  // Toggle language dropdown
  const toggleLanguageDropdown = () => {
    if (isCollapsed) {
      onToggle();
    } else {
      setShowLanguageDropdown(!showLanguageDropdown);
    }
  };

  // Handle time format toggle
  const handleTimeFormatToggle = () => {
    if (isCollapsed) {
      onToggle();
    } else if (onTimeFormatToggle) {
      onTimeFormatToggle();
    }
  };


  const navigationItems = [
    { icon: <Globe size={16} />, label: t('zones.title'), active: false, onClick: () => {
      if (isCollapsed) {
        onToggle();
      } else if (onOpenTimezoneGroups) {
        onOpenTimezoneGroups();
      }
    }},
    { icon: <Languages size={16} />, label: getCurrentLangDisplay(), active: false, onClick: toggleLanguageDropdown, isDropdown: true },
    { icon: <Clock size={16} />, label: use24HourFormat ? t('time.format.24hour') : t('time.format.12hour'), active: false, onClick: handleTimeFormatToggle },
    { icon: <Calendar size={16} />, label: t('sidebar.calendars'), active: false, onClick: () => {
      if (isCollapsed) {
        onToggle();
      } else if (onOpenCalendarPreferences) {
        onOpenCalendarPreferences();
      }
    }},
    { icon: <KeyRound size={16} />, label: t('sidebar.apiAccess'), active: window.location.pathname === '/settings', onClick: () => {
      navigate('/settings');
    }},
    // { icon: <CreditCard size={16} />, label: 'Pricing', active: false, onClick: () => {
    //   navigate('/pricing');
    // }},
    { icon: <HelpCircle size={16} />, label: t('buttons.help'), active: false, onClick: () => {
      if (isCollapsed) {
        onToggle();
      } else if (onToggleHelp) {
        onToggleHelp();
      }
    }},
  ];

  return (
    <aside className={`
      ${isCollapsed ? 'w-14' : 'w-52'} 
      h-screen bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-[width] duration-300 ease-in-out
    `}>
      <div className={`${isCollapsed ? 'p-2' : 'p-4'}`}>
        {/* Navigation */}
        <nav className="space-y-1">
          {navigationItems.map((item, index) => (
            <div key={index} className="relative">
              <div
                onClick={() => {
                  if (item.onClick) {
                    item.onClick();
                  }
                }}
                className={`
                  flex items-center ${isCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2 text-sm rounded-md cursor-pointer transition-colors
                  ${item.active 
                    ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100' 
                    : 'text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }
                `}
              >
                <span className={`flex items-center justify-center ${isCollapsed ? 'w-full' : ''}`}>{item.icon}</span>
                {!isCollapsed && (
                  <>
                    <span className="font-medium flex-1">{item.label}</span>
                    {item.isDropdown && <ChevronDown size={14} />}
                  </>
                )}
              </div>
              
              {/* Language Dropdown */}
              {item.isDropdown && showLanguageDropdown && !isCollapsed && (
                <div
                  ref={dropdownRef}
                  className="absolute left-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50"
                >
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`
                        w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
                        ${i18n.language?.startsWith(lang.code) ? 'bg-gray-100 dark:bg-gray-700' : ''}
                      `}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
}